import React from 'react';

import arrayMove from 'array-move';
import {SortableContainerProps, SortEnd, SortEvent} from 'react-sortable-hoc';

import DragHandle from './DragHandle';
import List, {ListProps} from './List';
import ListItem from './List/ListItem';

type Item = ListProps['items'][number];

export type SortableListProps<T extends Item = Item> = Pick<ListProps<T>, 'items' | 'children'> &
  Pick<
    SortableContainerProps,
    | 'lockAxis'
    | 'distance'
    | 'onSortStart'
    | 'onSortEnd'
    | 'useDragHandle'
    | 'pressDelay'
    | 'useWindowAsScrollContainer'
    | 'getContainer'
  > & {
    classes?: ListProps['classes'];
    onChange: (items: ListProps<T>['items']) => void;
  };

const SortableList = <T extends Item>({
  classes,
  items,
  children,
  onSortEnd,
  onSortStart,
  onChange,
  lockAxis,
  distance,
  useDragHandle,
  pressDelay,
  useWindowAsScrollContainer,
  getContainer,
}: SortableListProps<T>) => {
  const handleOnSortEnd = (handler: SortEnd, evt: SortEvent) => {
    const newOrder = arrayMove(items, handler.oldIndex, handler.newIndex);
    onChange(newOrder);
    onSortEnd(handler, evt);
  };

  return (
    <List
      getContainer={getContainer}
      useWindowAsScrollContainer={useWindowAsScrollContainer}
      pressDelay={pressDelay}
      useDragHandle={useDragHandle}
      classes={classes}
      lockAxis={lockAxis}
      distance={distance}
      items={items}
      onSortStart={onSortStart}
      onSortEnd={handleOnSortEnd}
    >
      {children}
    </List>
  );
};

SortableList.defaultProps = {
  lockAxis: 'y',
  distance: 0,
  children: ({item, sortIndex}) => (
    <ListItem>
      <DragHandle disabled={item.disabledDrag} />
      {sortIndex && `${sortIndex}.`} {item && `${item.label}`}
    </ListItem>
  ),
  onSortStart: () => null,
  onSortEnd: () => null,
};

export default SortableList;
